import { CalendarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Table as AntTable, Button, Space, Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { ReactNode, useState } from 'react';

import {
    AuditEntityType,
    DirectRequestFallback,
    FacilityDto,
    PayPeriodDto,
    ShiftListItemDto,
    ShiftStatus,
    ShiftType,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import {
    getShiftDuration,
    getTextFromQuillDeltas,
    HumlaDateTime,
    roundToAtMostTwoDecimals,
    toDateISOTimeStr,
    toHourAndMinuteDisplay,
} from '@clh/util';

import { ActionButton } from '../../action-button';
import AuditHistoryButton from '../../audit-history/button';
import { getDateStrWithTimezone } from '../../helpers';
import { useSelection } from '../../hooks/use-selection';
import ObjectPropertyList from '../../record-details/object-property-list';

import ScheduleChangeModal from './schedule-change-modal';

const Text = Typography.Text;

export default function DetailsTab() {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const api = useApiClient();

    const { timezone } = selection.facility.address;

    const isDirectRequest = selection.directRequestFallback != null;
    const requestedNurse = isDirectRequest
        ? selection.requestedNurseProfiles?.[0]
        : undefined;
    const postedFor: ReactNode = (
        <div style={{ textAlign: 'right' }}>
            <Tag color={isDirectRequest ? 'blue' : 'green'}>
                {isDirectRequest ? 'Direct Request' : 'Posted to Network'}
            </Tag>
            {requestedNurse ? (
                <Button
                    type="link"
                    target="_blank"
                    href={`/profiles/${requestedNurse.id}`}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {requestedNurse.firstName} {requestedNurse.lastName}
                </Button>
            ) : isDirectRequest ? (
                'Specific Nurse'
            ) : (
                'Your Humla Nurses'
            )}
        </div>
    );
    const ifDeclined =
        selection.directRequestFallback === DirectRequestFallback.CancelShift
            ? 'Cancel the shift'
            : selection.directRequestFallback ===
                DirectRequestFallback.OpenToNetwork
              ? 'Open to Humla nurses who are a match'
              : null;

    return (
        <>
            <Space
                direction="horizontal"
                style={{ width: '100%', justifyContent: 'space-between' }}
            >
                <Typography.Title level={4}>Details</Typography.Title>
                <Button
                    icon={<CalendarOutlined />}
                    onClick={() => setScheduleModalOpen(true)}
                >
                    Change Schedule
                </Button>
            </Space>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <ObjectPropertyList
                    values={{
                        ...selection,
                        hoursScheduled:
                            getShiftDuration(
                                selection.shiftTime,
                                toDateISOTimeStr(selection.startTime),
                                toDateISOTimeStr(selection.endTime)
                            ) * 60,
                        postedFor,
                        ...(ifDeclined ? { ifDeclined } : {}),
                        ...(selection.onCallShift
                            ? {
                                  calledBackHoursWorked:
                                      selection.minutesWorked || 0,
                                  onCallHoursWorked:
                                      selection.onCallShift.minutesWorked || 0,
                              }
                            : {
                                  hoursWorked: selection.minutesWorked || 0,
                              }),
                    }}
                    fields={{
                        id: (id: string) => (
                            <Text code={true} copyable={true}>
                                {id}
                            </Text>
                        ),
                        facility: (facility: FacilityDto) => (
                            <>{facility.name}</>
                        ),
                        startTime: (startTime: Date) => (
                            <>{getDateStrWithTimezone(startTime, timezone)}</>
                        ),
                        endTime: (endTime: Date) => (
                            <>{getDateStrWithTimezone(endTime, timezone)}</>
                        ),
                        updatedAt: (updatedAt: Date) => (
                            <div style={{ textAlign: 'right' }}>
                                <AuditHistoryButton
                                    entityId={selection.id}
                                    entityType={AuditEntityType.Shift}
                                >
                                    {DateTime.fromJSDate(
                                        updatedAt,
                                        timezone
                                            ? { zone: timezone }
                                            : undefined
                                    ).toLocaleString(
                                        HumlaDateTime.DATETIME_SHORT
                                    )}
                                </AuditHistoryButton>
                            </div>
                        ),
                        shiftTime: true,
                        hoursScheduled: (minutes: number) => (
                            <>{toHourAndMinuteDisplay(minutes, true)}</>
                        ),
                        ...(selection.onCallShift
                            ? {
                                  calledBackHoursWorked: (minutes: number) => (
                                      <>
                                          {minutes
                                              ? toHourAndMinuteDisplay(
                                                    minutes,
                                                    true
                                                )
                                              : 'N/A'}
                                      </>
                                  ),
                                  onCallHoursWorked: (minutes: number) => (
                                      <>
                                          {minutes
                                              ? toHourAndMinuteDisplay(
                                                    minutes,
                                                    true
                                                )
                                              : 'N/A'}
                                      </>
                                  ),
                              }
                            : {
                                  hoursWorked: (minutes: number) => (
                                      <>
                                          {minutes
                                              ? toHourAndMinuteDisplay(
                                                    minutes,
                                                    true
                                                )
                                              : 'N/A'}
                                      </>
                                  ),
                              }),
                        shiftType: true,
                        specialtyCode: true,
                        status: (status) => (
                            <Tag
                                color={
                                    status === ShiftStatus.Canceled
                                        ? 'error'
                                        : 'success'
                                }
                            >
                                {status}
                            </Tag>
                        ),
                        licenseType: true,
                        postedFor: (val: ReactNode) => val,
                        ...(ifDeclined ? { ifDeclined: true } : {}),
                        description: (description) => (
                            <>{getTextFromQuillDeltas(description)}</>
                        ),
                        payPeriods: (payPeriods: PayPeriodDto[]) => (
                            <AntTable
                                size="small"
                                pagination={false}
                                columns={[
                                    {
                                        title: 'Pay Period',
                                        dataIndex: 'schedule',
                                        key: 'schedule',
                                    },
                                    {
                                        title: 'Time Submitted',
                                        dataIndex: 'minutesWorked',
                                        key: 'minutesWorked',
                                        render: (minutesWorked) => {
                                            // Check if this is an On-Call shift
                                            const isOnCall =
                                                selection.shiftType ===
                                                    ShiftType.OnCall &&
                                                selection.onCallShift;

                                            if (!minutesWorked) {
                                                return null;
                                            }

                                            if (isOnCall) {
                                                const onCallHours = selection
                                                    .onCallShift?.minutesWorked
                                                    ? roundToAtMostTwoDecimals(
                                                          selection.onCallShift
                                                              .minutesWorked /
                                                              60
                                                      )
                                                    : '0';
                                                const calledInHours =
                                                    roundToAtMostTwoDecimals(
                                                        minutesWorked / 60
                                                    );

                                                return (
                                                    <>
                                                        {onCallHours}hr
                                                        {selection.onCallShift
                                                            ?.minutesWorked !==
                                                        60
                                                            ? 's'
                                                            : ''}{' '}
                                                        On-Call
                                                        <br />
                                                        {calledInHours}hr
                                                        {minutesWorked > 60
                                                            ? 's'
                                                            : ''}{' '}
                                                        Called back
                                                    </>
                                                );
                                            } else {
                                                // For standard shifts, show total hours
                                                const hoursWorked =
                                                    roundToAtMostTwoDecimals(
                                                        minutesWorked / 60
                                                    );

                                                return (
                                                    <>
                                                        {hoursWorked}hr
                                                        {minutesWorked > 60
                                                            ? 's'
                                                            : ''}
                                                    </>
                                                );
                                            }
                                        },
                                    },
                                    {
                                        title: 'Paid',
                                        dataIndex: 'zealEmployeeCheckId',
                                        key: 'zealEmployeeCheckId',
                                        render: (zealEmployeeCheckId) =>
                                            zealEmployeeCheckId ? (
                                                <a
                                                    href={`https://payroll.humla.com/pending-checks/check/${zealEmployeeCheckId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <CheckCircleOutlined />
                                                </a>
                                            ) : null,
                                    },
                                    {
                                        title: 'Invoiced',
                                        dataIndex: 'stripeInvoiceId',
                                        key: 'stripeInvoiceId',
                                        render: (stripeInvoiceId) =>
                                            stripeInvoiceId ? (
                                                <a
                                                    href={`https://dashboard.stripe.com/invoices/${stripeInvoiceId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <CheckCircleOutlined />
                                                </a>
                                            ) : null,
                                    },
                                ]}
                                locale={{
                                    emptyText: (
                                        <ActionButton
                                            successMessage="Pay periods generated"
                                            action={async () => {
                                                await api?.payPeriodControllerCreatePayPeriod(
                                                    {
                                                        shiftId: selection.id,
                                                    }
                                                );

                                                const updated =
                                                    await api?.shiftControllerListShifts(
                                                        {
                                                            search: selection.id,
                                                        }
                                                    );

                                                if (
                                                    updated &&
                                                    updated.result.length > 0
                                                ) {
                                                    setSelection(
                                                        updated.result[0]
                                                    );
                                                }
                                            }}
                                        >
                                            Generate
                                        </ActionButton>
                                    ),
                                }}
                                dataSource={payPeriods.map((period) => ({
                                    schedule: `${period.startDate.toLocaleDateString()} - ${period.endDate.toLocaleDateString()}`,
                                    ...period,
                                }))}
                            />
                        ),
                    }}
                />
            </Space>

            <ScheduleChangeModal
                shift={selection}
                open={scheduleModalOpen}
                onClose={() => setScheduleModalOpen(false)}
                onSuccess={async () => {
                    const result = await api?.shiftControllerListShifts({
                        search: selection.id,
                    });

                    if (result?.result?.[0]) {
                        setSelection(result.result[0]);
                    }
                }}
            />
        </>
    );
}
